import { Kind, parse, print, SelectionSetNode, visit } from 'graphql';
import compact from 'lodash/compact';

const MAXIMUM_GRAPHQL_OBJECT_DEPTH_TO_KEEP = 1;
const DEFAULT_GRAPHQL_NESTED_FIELDS = ['customValues', 'maintenanceHistory'];
export const DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY = 2;

export const removeNestedFieldsExcept = (
  query: string,
  maxDepthToKeep: number = MAXIMUM_GRAPHQL_OBJECT_DEPTH_TO_KEEP,
  entitiesToRemove: string[] = [],
  keepFields: string[] = DEFAULT_GRAPHQL_NESTED_FIELDS,
  fieldsToRemove: string[] = []
) => {
  const ast = parse(query);

  const modifiedAst = visit(ast, {
    Field(node, _key, _parent, _path, ancestors) {
      const fieldName = node.name.value;
      const depth = ancestors.filter(
        (ancestor) =>
          typeof ancestor === 'object' && ancestor !== null && 'kind' in ancestor && ancestor.kind === Kind.FIELD
      ).length;

      if (fieldsToRemove.includes(fieldName)) {
        return null;
      }

      if (
        node.selectionSet &&
        !keepFields.includes(fieldName) &&
        depth >= maxDepthToKeep &&
        entitiesToRemove.includes(fieldName)
      ) {
        return null; // Remove the field
      }

      if (node.selectionSet && !keepFields.includes(fieldName) && depth >= maxDepthToKeep) {
        return {
          ...node,
          selectionSet: {
            kind: Kind.SELECTION_SET,
            selections: [
              {
                kind: Kind.FIELD,
                name: { kind: Kind.NAME, value: '__typename' },
              },
            ],
          },
        };
      }

      return undefined; // No change
    },
  });

  return print(modifiedAst);
};

export const addContactIntegrationsProperties = (query: string) => {
  if (query.includes('paymentProperties')) {
    return query;
  }
  return query.replace(
    /integrations\s*{\s*__typename\s*}/g,
    `integrations {
        paymentProperties {
          accountId
          __typename
        }
        __typename
      }`
  );
};

export const addCertificateValidityGracePeriod = (query: string): string =>
  query.replace(
    /certificateValidity\s*\{[^{}]*\}/g,
    `certificateValidity {
      gracePeriod {
        value
        validityUnit
        __typename
      }
      nonCompliant
      overlapping
      riskLevel
      __typename
    }`
  );

export const addMaintenanceSchemaProperties = (query: string): string =>
  query.replace(
    /maintenanceSchemas\s*\{[^{}]*\}/g,
    `maintenanceSchemas {
        id
        criticality
        deadline
        descriptions {
          language
          label
          __typename
        }
        interval {
          value
          validityUnit
          __typename
        }
        maintenanceType
        names {
          language
          label
          __typename
        }
        notificationType
        notificationsEnabled
        proofType
        proofTypeName {
          language
          label
          __typename
        }
        recurrence
        responsibleParty
        triggerType
        __typename
      }`
  );

export const addTechnicMaintenanceHistoryProperties = (query: string): string =>
  query.replace(
    /maintenanceHistory\s*\{[^{}]*\}/g,
    `maintenanceHistory {
      contactId
      customValues {
        name
        value
        __typename
      }
      date
      fileId
      id
      maintenanceSchemaId
      __typename
    }`
  );

export const addInvoiceIntegrationsProperties = (query: string) => {
  if (query.includes('paymentIntentId')) {
    return query;
  }
  return query.replace(
    /integrations\s*{\s*__typename\s*}/g,
    `integrations {
        paymentProperties {
          availableOn
          error
          feeAmount
          paymentIntentId
          paymentLinkAmount
          paymentLinkUrl
          payoutId
          statementDescriptor
          __typename
        }
        __typename
      }`
  );
};

export const addCustomFieldProperties = (query: string): string =>
  query.replace(
    /customFieldProperties(\s*{([^{}]|{[^{}]*})*})?/g,
    `customFieldProperties {
      isRequired
      isDefault
      defaultValue
      options {
        id
        labels {
          language
          label
          __typename
        }
        __typename
      }
      __typename
    }`
  );

export const addLinkedProperties = (query: string) => {
  if (query.includes('communicationSubscriptions')) {
    return query;
  }
  return query.replace(
    /linkProperties\s*{\s*__typename\s*}/g,
    `linkProperties {
        contact {
          communicationSubscriptions
        }
      }`
  );
};

const entityRelationFields = `
      items {
        id
        sourceId
        sourceTableName
        foreignId
        foreignTableName
        linkDescription
        linkProperties {
          contact {
            communicationSubscriptions
          }
        }
        clientId
        readers
        readId
        __typename
      }
      nextToken
      startedAt
      __typename`;

export const addContactEntities = (query: string) => {
  if (query.includes('sourceEntityRelations')) {
    return query;
  }
  if (!query.includes('contact')) {
    return query;
  }
  return query.replace(
    /(\bcontact\s*\{[^}]*)(\s*__typename\s*\})/,
    `$1        sourceEntityRelations {${entityRelationFields}
        }
        foreignEntityRelations {${entityRelationFields}
        }$2`
  );
};

export const addContactPhoneNumbers = (query: string): string => {
  if (query.includes('phoneNumbers')) {
    return query;
  }
  if (!query.includes('contact')) {
    return query;
  }
  return query.replace(
    /(\bcontact\s*\{[^}]*)(\s*__typename\s*\})/,
    `$1        phoneNumbers {
          countryCode
          isPrimary
          label
          number
          __typename
        }
$2`
  );
};

export const addInvoiceSentVersionChannels = (query: string): string =>
  query.replace(
    /(\bsentVersions\s*\{[^{}]*)(\s*__typename\s*\})/g,
    `$1        channels {
          channel
          status
          communicationId
          peppolId
          __typename
        }
$2`
  );

export const addChargesDetailAmountsToLeasePriceHistory = (query: string): string => {
  if (query.includes('chargesAmountsDetails')) {
    return query;
  }
  return query.replace(
    /amountDetails\s*\{([^{}]*)\}/g,
    (_, inner) => `amountDetails {${inner}        chargesAmountsDetails {
          leaseVariousOperationId
          previousMonthlyAmount
          newMonthlyAmount
          indexationBaseIndex
          indexationBaseAmount
          indexationBaseIndexDate
          indexed
          __typename
        }
      }`
  );
};

const EMPTY_SELECTION_SET: SelectionSetNode = { kind: Kind.SELECTION_SET, selections: [] };

const parseFieldSelectionSet = (fieldName: string, selectionSetSource: string): SelectionSetNode => {
  const document = parse(`{ ${fieldName} ${selectionSetSource} }`);
  const [operationDefinition] = document.definitions;
  const [selection] =
    operationDefinition.kind === Kind.OPERATION_DEFINITION ? operationDefinition.selectionSet.selections : [];
  return selection?.kind === Kind.FIELD && selection.selectionSet ? selection.selectionSet : EMPTY_SELECTION_SET;
};

const RULE_CONDITIONS_SELECTION_SET = parseFieldSelectionSet(
  'conditions',
  `{
    attribute {
      attributeName
      __typename
    }
    comparisonMode
    operator
    values
    valueKind
    __typename
  }`
);

const RULE_OUTCOME_SELECTION_SET = parseFieldSelectionSet(
  'outcome',
  `{
    interval {
      interval {
        value
        unit
        __typename
      }
      notifyLeadTime {
        value
        unit
        __typename
      }
      referenceAttribute {
        attributeName
        __typename
      }
      referenceFallbackAttribute {
        attributeName
        __typename
      }
      __typename
    }
    type
    __typename
  }`
);

export const addScheduleRuleProperties = (query: string): string => {
  const ast = parse(query);
  const modifiedAst = visit(ast, {
    Field(node) {
      if (node.name.value === 'conditions') {
        return { ...node, selectionSet: RULE_CONDITIONS_SELECTION_SET };
      }
      if (node.name.value === 'outcome') {
        return { ...node, selectionSet: RULE_OUTCOME_SELECTION_SET };
      }
      return undefined;
    },
  });
  return print(modifiedAst);
};

/** Root fields of a document holding only query operations; null when it does not parse or holds anything else. */
export const getQueryRootFieldNames = (query: string): string[] | null => {
  try {
    const rootFieldNames = parse(query).definitions.flatMap((definition) =>
      definition.kind === Kind.OPERATION_DEFINITION && definition.operation === 'query'
        ? definition.selectionSet.selections.map((selection) =>
            selection.kind === Kind.FIELD ? selection.name.value : null
          )
        : [null]
    );
    return rootFieldNames.includes(null) ? null : compact(rootFieldNames);
  } catch {
    return null;
  }
};
